// The invoice editor (UI kit W2): details on the left, the live A4 preview
// on the right. Every change autosaves shortly after typing stops. A new
// invoice is only stored, and only takes its number, once something has
// been entered, so opening the editor and leaving uses up nothing.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { takeNumber } from '@/app/actions';
import { useAppData } from '@/app/AppData';
import { usePageTitle } from '@/app/usePageTitle';
import { draftInvoice } from '@/core/business';
import { toSnapshot } from '@/core/client';
import { addDays, daysBetween, nowStamp, today } from '@/core/dates';
import { argbToHex, BRAND_COLOURS, TEMPLATES } from '@/core/design';
import { newId } from '@/core/ids';
import { currencyOfInvoice, type Discount, type Invoice, isDraft, itemAmount, type LineItem, NO_DISCOUNT, NO_TAX, taxLabel, totalsOf } from '@/core/invoice';
import { CURRENCIES, formatBasisPoints, formatQuantity, Money, parseBasisPoints, parseQuantity } from '@/core/money';
import { isRemembered, lineFromSaved, rememberedItem, type SavedItem } from '@/core/savedItem';
import { track } from '@/data/analytics';
import { deleteInvoice, saveInvoice, saveItem } from '@/data/store';
import { ScaledPaper } from '@/invoice/InvoicePaper';
import { PrintableInvoice, printInvoice } from '@/invoice/print';
import { currencyLabel, discountLabel, templateLabel } from '@/l10n/labels';
import { t } from '@/l10n/strings';
import { ClientPicker } from '@/pages/ClientPicker';
import { Button, PageHead, PageLoading, SelectField, Switch, TextArea, TextField } from '@/ui/controls';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Icon } from '@/ui/Icon';
import { useToast } from '@/ui/Toasts';
import { NotFoundPage } from '@/pages/System';

type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'failed';

/** What's typed into an item row, kept as text so half-typed numbers stay put. */
interface Row {
  id: string;
  name: string;
  description: string;
  quantity: string;
  rate: string;
}

const AUTOSAVE_MS = 800;

export function InvoiceEditorPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { invoices, business, clients, user } = useAppData();
  // Wait for the invoice (or the business, for a new one) before starting.
  if (!user || business === undefined || invoices === undefined || clients === undefined) return <PageLoading />;
  if (!business) return <NotFoundPage />;
  if (id) {
    const existing = invoices.find((i) => i.id === id);
    if (!existing) return <NotFoundPage />;
    return <Editor key={id} uid={user.uid} start={existing} isNew={false} />;
  }
  let start = draftInvoice(business, { id: newId(), today: today(), now: nowStamp() });
  const client = clients.find((c) => c.id === params.get('clientId'));
  if (client) start = { ...start, clientId: client.id, client: toSnapshot(client) };
  return <Editor key="new" uid={user.uid} start={start} isNew />;
}

function rowsOf(invoice: Invoice): Row[] {
  const currency = currencyOfInvoice(invoice);
  return invoice.items.map((i) => ({
    id: i.id,
    name: i.name,
    description: i.description,
    quantity: formatQuantity(i.quantity),
    rate: new Money(i.rateMinor, currency).format({ withSymbol: false }),
  }));
}

function Editor({ uid, start, isNew: startsNew }: { uid: string; start: Invoice; isNew: boolean }) {
  const { business, proUnlocked, isPro, clients = [] } = useAppData();
  const navigate = useNavigate();
  const toast = useToast();
  const [invoice, setInvoice] = useState(start);
  const [rows, setRows] = useState<Row[]>(() => (start.items.length ? rowsOf(start) : [{ id: newId(), name: '', description: '', quantity: '1', rate: '' }]));
  const [isNew, setIsNew] = useState(startsNew);
  const [status, setStatus] = useState<SaveStatus>(startsNew ? 'idle' : 'saved');
  const [showErrors, setShowErrors] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  usePageTitle(startsNew ? t('editorNewInvoice') : `${t('editorEditInvoice')} ${invoice.number}`);

  // Saving: one save at a time, the newest version last.
  const latest = useRef(invoice);
  const version = useRef(0);
  const savedVersion = useRef(0);
  const numberReserved = useRef(!startsNew);
  const madeEarlier = useRef(!startsNew);
  const editCounted = useRef(false);
  const saving = useRef<Promise<void>>(Promise.resolve());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deleted = useRef(false);

  const save = useCallback(() => {
    saving.current = saving.current.then(async () => {
      if (deleted.current || version.current === savedVersion.current) return;
      const target = version.current;
      setStatus('saving');
      try {
        let toSave = latest.current;
        if (!numberReserved.current) {
          const number = await takeNumber(uid, toSave.businessId);
          numberReserved.current = true;
          toSave = { ...toSave, number };
          latest.current = { ...latest.current, number };
          setInvoice((i) => ({ ...i, number }));
          track('invoice_created', { currency: toSave.currency, item_count: toSave.items.length, has_client: toSave.client.name.trim() !== '' });
        }
        saveInvoice(uid, toSave);
        // An earlier invoice changed here: counted once per visit.
        if (madeEarlier.current && !editCounted.current) {
          editCounted.current = true;
          track('invoice_edited', { was_draft: isDraft(toSave) });
        }
        // Once stored, the address becomes the invoice's own, so a reload
        // reopens it. Swapped in place: a route change would restart the editor.
        if (window.location.pathname === '/invoices/new') window.history.replaceState(window.history.state, '', `/invoices/${toSave.id}/edit`);
        savedVersion.current = target;
        setIsNew(false);
        setStatus(version.current === savedVersion.current ? 'saved' : 'pending');
      } catch (error) {
        console.error('Could not save the invoice', error);
        setStatus('failed');
      }
    });
    return saving.current;
  }, [uid]);

  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (!deleted.current && version.current !== savedVersion.current) return save();
    return saving.current;
  }, [save]);

  // Leaving the page saves what's there.
  useEffect(() => () => void flush(), [flush]);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (version.current !== savedVersion.current) {
        void flush();
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [flush]);

  const change = (update: (i: Invoice) => Invoice) => {
    const next = { ...update(latest.current), updatedAt: nowStamp() };
    latest.current = next;
    version.current++;
    setInvoice(next);
    setStatus('pending');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void save(), AUTOSAVE_MS);
  };

  const currency = currencyOfInvoice(invoice);
  const totals = totalsOf(invoice);

  // Rows become items: named rows only, with their last good numbers.
  const changeRows = (next: Row[]) => {
    setRows(next);
    const previous = new Map(latest.current.items.map((i) => [i.id, i]));
    const items: LineItem[] = next
      .filter((r) => r.name.trim())
      .map((r) => ({
        id: r.id,
        name: r.name.trim(),
        description: r.description.trim(),
        quantity: parseQuantity(r.quantity) ?? previous.get(r.id)?.quantity ?? 1,
        rateMinor: Money.tryParse(r.rate, currency)?.minor ?? previous.get(r.id)?.rateMinor ?? 0,
      }));
    change((i) => ({ ...i, items }));
  };

  const clientError = !invoice.client.name.trim() ? t('errorClientMissing') : null;
  const itemsError = invoice.items.length === 0 ? t('errorItemsMissing') : null;

  const finish = async () => {
    setShowErrors(true);
    if (clientError || itemsError) {
      document.querySelector<HTMLElement>('[aria-invalid="true"], [data-invalid="true"] input')?.focus();
      return;
    }
    await flush();
    track('invoice_previewed', { is_draft: isDraft(latest.current) });
    navigate(`/invoices/${latest.current.id}`);
  };

  const remove = async () => {
    if (timer.current) clearTimeout(timer.current);
    await saving.current;
    deleted.current = true;
    if (!isNew) deleteInvoice(uid, invoice.id);
    track('invoice_deleted', { was_draft: true, from: 'editor' });
    toast(t('invoicesDraftDeleted', { number: invoice.number }));
    navigate('/invoices', { replace: true });
  };

  const statusWord = {
    idle: t('editorStatusNew'),
    pending: t('editorStatusDraft'),
    saving: t('editorSaving'),
    saved: t('editorStatusAutosaved'),
    failed: t('editorNotSaved'),
  }[status];

  return (
    <div className="editor">
      <section className="editor__form page page--wide">
        <PageHead
          back={{ to: isDraft(invoice) ? '/invoices' : `/invoices/${invoice.id}`, label: isDraft(invoice) ? t('navInvoices') : invoice.number }}
          title={startsNew ? t('editorNewInvoice') : t('editorEditInvoice')}
          subtitle={
            <span aria-live="polite">
              {invoice.number} · <span className={status === 'failed' ? 'danger-text' : undefined}>{statusWord}</span>
            </span>
          }
          actions={
            <>
              <Button className="editor__preview-toggle" icon="eye" onClick={() => setShowPreview(true)}>
                {t('previewPreview')}
              </Button>
              {isDraft(invoice) && !isNew && (
                <Button variant="ghost" icon="trash" onClick={() => setConfirmDelete(true)}>
                  {t('editorDeleteDraft')}
                </Button>
              )}
              <Button variant="action" icon={isDraft(invoice) ? 'send' : 'check'} onClick={finish}>
                {isDraft(invoice) ? t('editorPreviewSend') : t('commonDone')}
              </Button>
            </>
          }
        />
        {!isDraft(invoice) && (
          <div className="banner banner--info">
            <Icon name="info" />
            <div className="banner__txt">
              <span style={{ color: 'var(--ink)' }}>{t('editorYourClientAlreadyHasThis')}</span>
            </div>
          </div>
        )}

        <div className="editor__grid">
          <ClientPicker
            uid={uid}
            clients={clients}
            client={invoice.client}
            clientId={invoice.clientId}
            error={showErrors ? clientError : null}
            onPick={(c) => change((i) => ({ ...i, clientId: c.id, client: toSnapshot(c) }))}
          />
          <SelectField
            label={t('setupCurrency')}
            value={invoice.currency}
            onChange={(e) => {
              const code = e.target.value;
              change((i) => ({ ...i, currency: code }));
            }}
          >
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} {c.symbol} · {currencyLabel(c.code)}
              </option>
            ))}
          </SelectField>
          <TextField
            label={t('editorIssued')}
            type="date"
            value={invoice.issueDate}
            max={today()}
            required
            onChange={(e) => {
              const picked = e.target.value;
              if (!picked) return;
              // Issued today at the latest; the due date moves with it,
              // keeping the payment terms, but never before today.
              const now = today();
              const issue = picked > now ? now : picked;
              change((i) => {
                const due = addDays(issue, daysBetween(i.issueDate, i.dueDate));
                return { ...i, issueDate: issue, dueDate: due < now ? now : due };
              });
            }}
          />
          <TextField
            label={t('editorDue')}
            type="date"
            value={invoice.dueDate}
            min={invoice.issueDate > today() ? invoice.issueDate : today()}
            required
            help={t('settingsPaymentTerms', { days: Math.max(0, daysBetween(invoice.issueDate, invoice.dueDate)) })}
            onChange={(e) => {
              const due = e.target.value;
              if (!due) return;
              // Due today at the earliest.
              const earliest = invoice.issueDate > today() ? invoice.issueDate : today();
              change((i) => ({ ...i, dueDate: due < earliest ? earliest : due }));
            }}
          />
        </div>

        <ItemsEditor rows={rows} currency={currency.code} onChange={changeRows} error={showErrors ? itemsError : null} />

        <div className="editor__bottom">
          <div className="stack-4 grow">
            <TextArea label={t('commonNotesToClient')} value={invoice.notes} rows={4} placeholder={t('editorOptionalEGPaymentInstructions')} onChange={(e) => change((i) => ({ ...i, notes: e.target.value }))} />
            <AdjustmentsEditor invoice={invoice} defaultTax={business?.tax ?? NO_TAX} onDiscount={(discount) => change((i) => ({ ...i, discount }))} onTax={(tax) => change((i) => ({ ...i, tax }))} />
          </div>
          <div className="card editor__totals" aria-live="polite">
            <div className="between body">
              <span className="muted">{t('editorSubtotal')}</span>
              <span className="num">{totals.subtotal.format()}</span>
            </div>
            {invoice.discount.type !== 'none' && (
              <div className="between body">
                <span className="muted">{discountLabel(invoice.discount)}</span>
                <span className="num">{totals.discount.negate().format()}</span>
              </div>
            )}
            {invoice.tax.basisPoints > 0 && (
              <div className="between body">
                <span className="muted">{taxLabel(invoice.tax)}</span>
                <span className="num">{totals.tax.format()}</span>
              </div>
            )}
            {totals.paid.isPositive && (
              <div className="between body">
                <span className="muted">{t('statusPaid')}</span>
                <span className="num">{totals.paid.negate().format()}</span>
              </div>
            )}
            <hr className="divider" />
            <div className="between">
              <b className="headline">{totals.paid.isPositive ? t('detailStillOwed') : t('editorTotalDue')}</b>
              <b className="amount-lg">{(totals.paid.isPositive ? totals.balance : totals.total).format()}</b>
            </div>
          </div>
        </div>
      </section>

      <aside className={`editor__preview${showPreview ? ' editor__preview--open' : ''}`} aria-label={t('editorLivePreviewA4')}>
        <div className="between">
          <span className="overline">{t('editorLivePreviewA4')}</span>
          <div className="row" style={{ gap: 6 }}>
            <Button size="sm" variant="ghost" icon="download" onClick={() => printInvoice(invoice)}>
              {t('detailSavePdf')}
            </Button>
            <button type="button" className="iconbtn iconbtn--sm editor__preview-close" aria-label={t('commonClose')} onClick={() => setShowPreview(false)}>
              <Icon name="x" size={18} />
            </button>
          </div>
        </div>
        <div className="row-wrap">
          <label className="vh" htmlFor="template">
            {t('customizeTemplate')}
          </label>
          <select
            id="template"
            className="input"
            style={{ width: 'auto', height: 36, fontSize: 13.5 }}
            value={invoice.design.templateId}
            onChange={(e) => {
              const templateId = e.target.value;
              change((i) => ({ ...i, design: { ...i.design, templateId } }));
            }}
          >
            {TEMPLATES.filter((tpl) => proUnlocked || !tpl.isPro || tpl.id === invoice.design.templateId).map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {templateLabel(tpl.id)}
              </option>
            ))}
          </select>
          <div className="swatches" role="group" aria-label={t('customizeBrandColour')}>
            {BRAND_COLOURS.map((argb) => (
              <button
                key={argb}
                type="button"
                className="swatch swatch--sm"
                style={{ background: argbToHex(argb) }}
                aria-pressed={(invoice.design.brandColor >>> 0) === argb}
                aria-label={t('customizeColourLabel', { hex: argbToHex(argb) })}
                onClick={() => change((i) => ({ ...i, design: { ...i.design, brandColor: argb } }))}
              />
            ))}
          </div>
        </div>
        {business && <ScaledPaper invoice={invoice} business={business} isPro={isPro} />}
      </aside>
      {business && <PrintableInvoice invoice={invoice} business={business} isPro={isPro} />}
      <ConfirmDialog open={confirmDelete} danger title={t('editorDeleteThisDraft')} message={t('editorItWillBeRemovedFrom')} confirmLabel={t('editorDeleteDraft')} onConfirm={remove} onClose={() => setConfirmDelete(false)} />
    </div>
  );
}

function ItemsEditor({ rows, currency, onChange, error }: { rows: Row[]; currency: string; onChange: (rows: Row[]) => void; error: string | null }) {
  const { user, business, items = [], proUnlocked } = useAppData();
  const navigate = useNavigate();
  const toast = useToast();
  const [picking, setPicking] = useState(false);
  const lastName = useRef<HTMLInputElement>(null);
  const [focusNew, setFocusNew] = useState(false);
  useEffect(() => {
    if (focusNew) {
      lastName.current?.focus();
      setFocusNew(false);
    }
  }, [focusNew]);
  const update = (id: string, patch: Partial<Row>) => onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const add = () => {
    onChange([...rows, { id: newId(), name: '', description: '', quantity: '1', rate: '' }]);
    setFocusNew(true);
  };
  const money = useMemo(() => CURRENCIES.find((c) => c.code === currency)!, [currency]);
  // Pro features open for everyone before Pro launches; the paywall after.
  const openPicker = () => (proUnlocked ? setPicking(true) : navigate('/pro'));
  const pick = (item: SavedItem) => {
    const line = lineFromSaved(item);
    onChange([...rows.filter((r) => r.name.trim() || r.rate.trim()), { id: line.id, name: line.name, description: line.description, quantity: '1', rate: new Money(line.rateMinor, money).format({ withSymbol: false }) }]);
    if (user) saveItem(user.uid, { ...item, updatedAt: nowStamp() });
    track('saved_item_used');
    setPicking(false);
  };
  const remember = (row: Row, rateMinor: number) => {
    if (!user) return;
    if (!proUnlocked) {
      navigate('/pro');
      return;
    }
    const { item, isNew } = rememberedItem(items, { name: row.name, description: row.description, rateMinor }, currency, business?.id ?? null);
    saveItem(user.uid, item);
    track('saved_item_saved', { is_new: isNew });
    toast(t('webSavedToMyItems', { name: item.name }));
  };
  return (
    <section className="stack-2" aria-labelledby="items-title" data-invalid={error ? true : undefined}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 className="label ink2" id="items-title">
          {t('editorItems')}
        </h2>
        <button type="button" className="link-btn row" style={{ gap: 6 }} onClick={openPicker}>
          <Icon name="package" size={16} />
          {t('editorFromMyItems')}
        </button>
      </div>
      {picking && <SavedItemPicker items={items} currency={currency} onPick={pick} onClose={() => setPicking(false)} />}
      <div className="table-wrap">
        <table className="table items-table">
          <thead>
            <tr>
              <th>{t('editorItem')}</th>
              <th className="end" style={{ width: 84 }}>
                {t('editorQuantity')}
              </th>
              <th className="end" style={{ width: 130 }}>
                {t('editorRate')} ({money.symbol})
              </th>
              <th className="end" style={{ width: 130 }}>
                {t('editorAmount')}
              </th>
              <th style={{ width: 84 }}>
                <span className="vh">{t('editorRemoveItem')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const quantity = parseQuantity(row.quantity);
              const rate = Money.tryParse(row.rate, money);
              const amount = quantity !== null && rate ? itemAmount({ id: row.id, name: row.name, description: '', quantity, rateMinor: rate.minor }, money) : null;
              const qtyBad = row.quantity.trim() !== '' && quantity === null;
              const rateBad = row.rate.trim() !== '' && rate === null;
              const saved = proUnlocked && rate !== null && row.name.trim() !== '' && isRemembered(items, { name: row.name, description: row.description, rateMinor: rate.minor }, currency);
              return (
                <tr key={row.id}>
                  <td>
                    <input
                      ref={index === rows.length - 1 ? lastName : undefined}
                      className="cell-input cell-input--strong"
                      value={row.name}
                      placeholder={t('editorSayWhatThisItemIs')}
                      aria-label={t('editorItem')}
                      aria-invalid={error && index === 0 && !row.name.trim() ? true : undefined}
                      onChange={(e) => update(row.id, { name: e.target.value })}
                    />
                    <input
                      className="cell-input cell-input--muted"
                      value={row.description}
                      placeholder={t('editorDescription')}
                      aria-label={t('editorDescription')}
                      onChange={(e) => update(row.id, { description: e.target.value })}
                    />
                  </td>
                  <td className="end">
                    <input className="cell-input cell-input--num" inputMode="decimal" value={row.quantity} aria-label={t('editorQuantity')} aria-invalid={qtyBad || undefined} title={qtyBad ? t('editorMoreThan0') : undefined} onChange={(e) => update(row.id, { quantity: e.target.value })} />
                  </td>
                  <td className="end">
                    <input className="cell-input cell-input--num" inputMode="decimal" value={row.rate} placeholder="0" aria-label={t('editorRate')} aria-invalid={rateBad || undefined} title={rateBad ? t('editorEnterAPriceLike25') : undefined} onChange={(e) => update(row.id, { rate: e.target.value })} />
                  </td>
                  <td className="end amount">{amount ? amount.format() : '—'}</td>
                  <td className="end">
                    <div className="row" style={{ gap: 2, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className={`iconbtn iconbtn--sm${saved ? ' iconbtn--saved' : ''}`}
                        aria-label={saved ? t('webInMyItems') : t('webSaveToMyItems')}
                        aria-pressed={saved}
                        title={saved ? t('webInMyItems') : t('webSaveToMyItems')}
                        disabled={!row.name.trim() || !rate}
                        onClick={() => rate && !saved && remember(row, rate.minor)}
                      >
                        <Icon name={saved ? 'check' : 'bookmark'} size={16} />
                      </button>
                      <button type="button" className="iconbtn iconbtn--sm" aria-label={t('editorRemoveItem')} onClick={() => onChange(rows.filter((r) => r.id !== row.id))}>
                        <Icon name="x" size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="table-foot">
          <button type="button" className="link-btn row" style={{ gap: 6 }} onClick={add}>
            <Icon name="plus" size={18} stroke={2.2} />
            {rows.length ? t('editorAddItem') : t('editorAddYourFirstItem')}
          </button>
        </div>
      </div>
      {error && (
        <span className="error" role="alert">
          {error}
        </span>
      )}
    </section>
  );
}

/** "Add from my items": the saved items in the invoice's currency, searchable. */
function SavedItemPicker({ items, currency, onPick, onClose }: { items: SavedItem[]; currency: string; onPick: (item: SavedItem) => void; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const inCurrency = items.filter((i) => i.currency === currency);
  const visible = inCurrency.filter((i) => !q || i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q));
  return (
    <Dialog open onClose={onClose} title={t('itemsPickTitle')}>
      {items.length === 0 ? (
        <div className="stack-2" style={{ textAlign: 'center', padding: '12px 0' }}>
          <strong>{t('itemsNoneTitle')}</strong>
          <p className="muted">{t('itemsNoneBody')}</p>
        </div>
      ) : (
        <div className="stack-3">
          <label className="search">
            <Icon name="search" size={18} />
            <span className="vh">{t('itemsSearchHint')}</span>
            <input type="search" autoFocus value={query} placeholder={t('itemsSearchHint')} onChange={(e) => setQuery(e.target.value)} />
          </label>
          {visible.length === 0 ? (
            <p className="muted" style={{ textAlign: 'center' }}>
              {q ? t('itemsNoMatch', { query: query.trim() }) : t('itemsNoneTitle')}
            </p>
          ) : (
            <ul className="saved-items">
              {visible.map((item) => (
                <li key={item.id}>
                  <button type="button" className="saved-items__row" onClick={() => onPick(item)}>
                    <span className="saved-items__name">
                      <strong>{item.name}</strong>
                      {item.description && <span className="muted">{item.description}</span>}
                    </span>
                    <span className="amount">{new Money(item.rateMinor, CURRENCIES.find((c) => c.code === item.currency)!).format()}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {inCurrency.length < items.length && <p className="small muted">{t('itemsOtherCurrency', { currency })}</p>}
        </div>
      )}
    </Dialog>
  );
}

function AdjustmentsEditor({ invoice, defaultTax, onDiscount, onTax }: { invoice: Invoice; defaultTax: { name: string; basisPoints: number }; onDiscount: (d: Discount) => void; onTax: (tax: { name: string; basisPoints: number }) => void }) {
  const currency = currencyOfInvoice(invoice);
  const [discountText, setDiscountText] = useState(() =>
    invoice.discount.type === 'percent' ? formatBasisPoints(invoice.discount.basisPoints).replace('%', '') : invoice.discount.type === 'amount' ? new Money(invoice.discount.amountMinor, currency).format({ withSymbol: false }) : '',
  );
  const [taxName, setTaxName] = useState(invoice.tax.name || defaultTax.name || t('taxDefaultName'));
  const [taxRate, setTaxRate] = useState(formatBasisPoints(invoice.tax.basisPoints || defaultTax.basisPoints || 750).replace('%', ''));
  const discountType = invoice.discount.type;
  const applyDiscount = (type: Discount['type'], text: string) => {
    setDiscountText(text);
    if (type === 'percent') {
      const bp = parseBasisPoints(text);
      onDiscount(bp && bp > 0 ? { type: 'percent', basisPoints: bp, amountMinor: 0 } : { type: 'percent', basisPoints: 0, amountMinor: 0 });
    } else if (type === 'amount') {
      const amount = Money.tryParse(text, currency);
      onDiscount({ type: 'amount', basisPoints: 0, amountMinor: amount?.minor ?? 0 });
    } else onDiscount(NO_DISCOUNT);
  };
  const applyTax = (on: boolean, name = taxName, rate = taxRate) => {
    const bp = parseBasisPoints(rate);
    onTax(on && bp && bp > 0 ? { name: name.trim() || t('taxDefaultName'), basisPoints: bp } : NO_TAX);
  };
  const taxOn = invoice.tax.basisPoints > 0;
  return (
    <div className="card card--tight stack-2">
      <div className="grid-2" style={{ alignItems: 'end' }}>
        <SelectField label={t('discountTitle')} value={discountType} onChange={(e) => applyDiscount(e.target.value as Discount['type'], '')}>
          <option value="none">{t('commonNone')}</option>
          <option value="percent">{t('discountPercentage')}</option>
          <option value="amount">{t('discountAmountInCurrency', { symbol: currency.symbol })}</option>
        </SelectField>
        {discountType !== 'none' && (
          <TextField
            label={discountType === 'percent' ? t('discountPercentLabel') : t('discountAmountLabel')}
            inputMode="decimal"
            value={discountText}
            placeholder={discountType === 'percent' ? t('discountPercentHint') : t('discountAmountHint')}
            onChange={(e) => applyDiscount(discountType, e.target.value)}
          />
        )}
      </div>
      <Switch label={t('taxTitle')} detail={t('taxAfterDiscountNote')} checked={taxOn} onChange={(on) => applyTax(on)} />
      {taxOn && (
        <div className="grid-2">
          <TextField
            label={t('taxName')}
            value={taxName}
            onChange={(e) => {
              setTaxName(e.target.value);
              applyTax(true, e.target.value);
            }}
          />
          <TextField
            label={t('taxRatePercentLabel')}
            inputMode="decimal"
            value={taxRate}
            error={parseBasisPoints(taxRate) === null ? t('taxRateError') : null}
            onChange={(e) => {
              setTaxRate(e.target.value);
              if (parseBasisPoints(e.target.value)) applyTax(true, taxName, e.target.value);
            }}
          />
        </div>
      )}
    </div>
  );
}
